import { Component, ViewEncapsulation, OnDestroy, OnInit, inject, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { AuthService } from '../../service/auth.service';
import { ActionState } from '../../model/enums/auth-state.enum';
import { fadeInGlideDown, fadeInGlideUp } from '@shared/utils/animations';
import { AuthMessageResponse } from '../../model/interfaces/auth-message-response.interface';
import { AuthPayloadMfaVerifyCode } from '../../model/interfaces/auth-payload-mfa-verivy-code.interface';
import { ActivatedRoute } from '@angular/router';
import { ENVIRONMENT } from '@env/environment';
import { AuthProcess } from '../../model/enums/auth.process';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { EventService } from '@shared/service/event.service';
import { AuthResponseMfaData } from '../../model/interfaces/auth-response-mfa-data.interface';
import { MfaVerificationComponent } from '@shared/components/mfa-verification/mfa-verification.component';
import {
  MfaSendCodeEvent,
  MfaVerifyCodeEvent,
  MfaConfig,
  MfaMethod,
} from '@shared/components/mfa-verification/mfa-verification.interface';

/* eslint-disable @angular-eslint/prefer-standalone */
@Component({
  selector: 'app-reset-password',
  templateUrl: './mfa.component.html',
  styleUrl: './mfa.component.scss',
  animations: [fadeInGlideDown(), fadeInGlideUp()],
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class MfaComponent implements OnInit, OnDestroy {
  @ViewChild('mfaVerification') mfaVerification!: MfaVerificationComponent;

  protected authService = inject(AuthService);
  private route = inject(ActivatedRoute);
  private event = inject(EventService);
  private translate = inject(TranslateService);

  state: ActionState = ActionState.PENDING;
  messages: AuthMessageResponse[] = [];
  authProcess = AuthProcess;
  isForceMfa = false;
  loading = false;
  initializing = true; // Show loading while fetching MFA options
  private redirectUrl?: string;

  // Form required by AuthService interface (validation is handled by shared component)
  form: FormGroup = new FormGroup({
    validationCode: new FormControl(''),
  });

  // Config for the shared MFA verification component
  mfaConfig!: MfaConfig;

  private readonly destroy$ = new Subject<void>();

  readonly icons = {
    bAdguard: 'fa-shield-check',
    pencil: 'fa-pencil',
  };

  constructor() {
    this.route.queryParamMap.subscribe((queryParams) => {
      this.redirectUrl = queryParams.get('next') || undefined;
    });

    this.route.paramMap.subscribe((params) => {
      if (params.get('project')) {
        this.authService.projectName = params.get('project') || '';
      }
    });

    this.isForceMfa = this.authService.mfaType === AuthProcess.MFA_ENFORCE;

    // Set initial fallback config (will be updated in ngOnInit after fetching options)
    this.mfaConfig = {
      availableMethods: ['phone', 'email'],
      expirationTimeMinutes: 5,
      defaultMethod: 'phone',
    };
  }

  async ngOnInit(): Promise<void> {
    // For regular MFA (not force), fetch the MFA options to get default_option
    if (!this.isForceMfa && !this.authService.mfaOptions) {
      await this.fetchMfaOptions();
    }
    this.updateMfaConfig();
    this.initializing = false;
  }

  private async fetchMfaOptions(): Promise<void> {
    try {
      const param = {
        username: this.authService.username,
        password: this.authService.password,
        project: this.authService.projectName || null,
      };
      this.authService.mfaOptions = await this.authService.getMfaOptions(param);
    } catch (error) {
      console.error('[MFA] Failed to fetch MFA options:', error);
    }
  }

  private updateMfaConfig(): void {
    const options = this.authService.mfaOptions;
    console.log('[MFA] updateMfaConfig - authService.mfaOptions:', options);

    if (options) {
      const availableMethods: MfaMethod[] = options.options.filter(
        (opt): opt is MfaMethod => opt === 'phone' || opt === 'email',
      );

      this.mfaConfig = {
        availableMethods,
        expirationTimeMinutes: options.expiration_time || 5,
        defaultMethod: (options.default_option as MfaMethod) || availableMethods[0],
      };
      console.log('[MFA] updateMfaConfig - using options, defaultMethod:', this.mfaConfig.defaultMethod);
    } else {
      console.log('[MFA] updateMfaConfig - no options available, keeping fallback config');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Handle send code from shared component (force MFA flow only)
  onSendCode(event: MfaSendCodeEvent): void {
    // This is only used for force MFA flow (setting up MFA for the first time)
    this.loading = true;

    const payload = {
      username: this.authService.username,
      password: this.authService.password,
      project: this.authService.projectName,
      verificationType: event.method,
      verificationSource: event.verificationSource,
    };

    this.authService.sendVerificationCode(payload, (_result, error) => {
      this.loading = false;

      if (error) {
        this.event.emit('toast-error', {
          header: 'Error',
          payload: 'Cannot create MFA instance, please contact administrator',
        });
        console.error(error);
        return;
      }

      // Update auth service MFA options
      this.authService.mfaOptions = {
        options: [event.method],
        expiration_time: 5,
        activated_phone: event.method === 'phone',
        activated_email: event.method === 'email',
        activated_google_app: false,
        default_option: event.method,
      } as AuthResponseMfaData;

      // Transition to verify step
      this.mfaVerification.goToVerifyStep(5);
    });
  }

  // Handle verify code from shared component
  onVerifyCode(event: MfaVerifyCodeEvent): void {
    this.loading = true;

    if (this.isForceMfa) {
      // Force MFA verify
      const payload = {
        username: this.authService.username,
        password: this.authService.password,
        project: this.authService.projectName,
        verificationSource: event.verificationSource,
        verificationType: event.method,
        codeToCheck: event.verificationCode,
        activeVerificationData: event.verificationSource,
        defaultOption: event.method,
      };

      this.authService.verifyCode(payload, (_, error) => {
        this.loading = false;

        if (error) {
          console.error(error);
          this.authService.finalizeAction(this, ActionState.FAILURE, ['mfa_wrong_code']);
          this.event.emit('toast-error', { header: 'Error', payload: 'Issue while updating your new MFA data' });
          return;
        }

        this.authService.setState(this, ActionState.SUCCESS);
        this.event.emit('toast', {
          header: this.translate.instant('AUTH_MFA_HEADER'),
          payload: this.translate.instant('AUTH_MFA_SETTINGS_SAVED'),
        });
        this.authService.navigateTo(this.redirectUrl || ENVIRONMENT.DEFAULT_VIEW.route, { replaceUrl: true });
      });
    } else {
      // Regular MFA verify
      const param: AuthPayloadMfaVerifyCode = {
        username: this.authService.username,
        password: this.authService.password,
        project: this.authService.projectName,
        receivedCode: event.verificationCode,
      };

      this.authService
        .authMfaVerifyCode(param)
        .then(() => {
          this.loading = false;
          this.authService.setState(this, ActionState.SUCCESS);
          this.authService.navigateTo(this.redirectUrl || ENVIRONMENT.DEFAULT_VIEW.route, { replaceUrl: true });
        })
        .catch((error: unknown) => {
          this.loading = false;
          const errorMessage = this.translate.instant('AUTH_MFA_ERROR_VERIFY_CODE');
          this.event.emit('toast-error', { header: 'Error', payload: errorMessage });
          console.error(error);
          this.authService.finalizeAction(this, ActionState.FAILURE, error as string[]);
        });
    }
  }

  // Handle resend code from shared component
  onResendCode(event: MfaSendCodeEvent): void {
    this.loading = true;

    const payload = {
      username: this.authService.username,
      password: this.authService.password,
      project: this.authService.projectName || null,
      resendVia: event.method,
      verificationSource: event.verificationSource || undefined,
    };

    this.authService
      .authMfaResendCode(payload)
      .then(() => {
        this.loading = false;
        this.mfaVerification.restartTimer(5);
        const successMessage = this.translate.instant('AUTH_MFA_CODE_SENT_SUCCESSFULLY');
        this.event.emit('toast', { header: 'Success', payload: successMessage });
      })
      .catch((error: unknown) => {
        this.loading = false;
        const errorKey = error instanceof Error ? error.message : 'server_error';
        let errorMessage = this.translate.instant('AUTH_ERROR_' + errorKey.toUpperCase());
        // Fallback to server error if translation key doesn't exist (returns the key itself)
        if (errorMessage === 'AUTH_ERROR_' + errorKey.toUpperCase()) {
          errorMessage = this.translate.instant('AUTH_ERROR_SERVER_ERROR');
        }
        this.event.emit('toast-error', { header: 'Error', payload: errorMessage });
      });
  }

  // Handle method change in verify step (regular MFA flow)
  onMethodChanged(event: MfaSendCodeEvent): void {
    this.loading = true;

    const payload = {
      username: this.authService.username,
      password: this.authService.password,
      project: this.authService.projectName || null,
      resendVia: event.method,
    };

    this.authService
      .authMfaResendCode(payload)
      .then(() => {
        this.loading = false;
        this.mfaVerification.restartTimer(this.mfaConfig.expirationTimeMinutes);
        const successMessage = this.translate.instant('AUTH_MFA_CODE_SENT_SUCCESSFULLY');
        this.event.emit('toast', { header: 'Success', payload: successMessage });
      })
      .catch((error: unknown) => {
        this.loading = false;
        const errorKey = error instanceof Error ? error.message : 'server_error';
        let errorMessage = this.translate.instant('AUTH_ERROR_' + errorKey.toUpperCase());
        if (errorMessage === 'AUTH_ERROR_' + errorKey.toUpperCase()) {
          errorMessage = this.translate.instant('AUTH_ERROR_SERVER_ERROR');
        }
        this.event.emit('toast-error', { header: 'Error', payload: errorMessage });
      });
  }

  // Handle back from shared component
  onBack(): void {
    // Go back to method selection (for force MFA flow)
    this.mfaVerification.goBackToSelect();
  }

  // Get the code sent message based on MFA options
  getCodeSentMessage(): string {
    const options = this.authService.mfaOptions;
    if (!options) return '';

    if (options.default_option === 'phone') {
      return 'Verification code sent to your phone';
    } else if (options.default_option === 'email') {
      return 'Verification code sent to your email';
    }
    return '';
  }
}
